/**
 * Quantity-based pricing for form blocks ("הרשמה בתשלום").
 *
 * A number field can be turned into a PRICE DRIVER: the first unit costs
 * `firstPrice`, every additional unit costs `extraPrice`. Typing 3 into
 * "כמות משתתפים" with 50/25 charges 50 + 25 + 25 = ₪100, and the campaign's
 * amount input turns into a locked total.
 *
 * The same field can also REPEAT a sub-field once per unit — e.g. a
 * "שם האורח" text box per participant — so the org collects one name per
 * seat without authoring N fields by hand.
 *
 * Both features are opt-in per field. A campaign with no priced field
 * behaves exactly as it did before this module existed (computeQuantityTotal
 * returns null → the legacy tier/custom-amount path stays in charge).
 *
 * Storage: repeat answers are written into the same `customFieldValues` map
 * the rest of the form uses, keyed by LABEL + index ("שם האורח 2"). Label
 * keys are what the campaign dashboard's legacy-key column builder and the
 * receipt template's {שדה.X} resolver already understand, so answers show up
 * for the org with no backend work.
 */

export interface QuantityPricing {
  /** Price of the first unit (incl. everything up to includedUnits). */
  firstPrice: number;
  /** Price of every unit beyond includedUnits. */
  extraPrice: number;
  /** How many units the firstPrice covers. Default 1. */
  includedUnits?: number;
  /** Per-currency override; falls back to the ILS-only fields above. */
  byCurrency?: Record<string, { firstPrice?: number; extraPrice?: number }>;
  /** Render a "סה״כ לתשלום" line under the field. Default true. */
  showTotalInForm?: boolean;
}

export type RepeatFieldType = 'text' | 'tel' | 'email' | 'idNumber' | 'number';

/**
 * Optional SMALL companion input rendered beside each repeated sub-field —
 * e.g. an age box next to every participant name. Answers use the same
 * label+index key convention ("גיל 2"), so they surface in the dashboard
 * and receipt merge fields exactly like the main repeat answers.
 */
export interface RepeatSecondConfig {
  label: string;
  labelI18n?: { he?: string; en?: string };
  type?: RepeatFieldType;
  required?: boolean;
}

export interface RepeatConfig {
  /** Base label — rendered as "שם האורח 2", "שם האורח 3", … */
  label: string;
  labelI18n?: { he?: string; en?: string };
  type?: RepeatFieldType;
  required?: boolean;
  /** First unit is the donor themselves → ask for units 2..n only. */
  skipFirst?: boolean;
  /** Hard cap on how many sub-fields render. Default REPEAT_MAX. */
  max?: number;
  /** Small companion input beside each row (e.g. גיל). */
  second?: RepeatSecondConfig;
}

/** Safety cap — a typo'd 9999 must never render 9999 inputs. */
export const REPEAT_MAX = 30;

/** Highest quantity we'll ever price/repeat, even without an authored max. */
export const QUANTITY_MAX = 999;

const num = (v: any): number | undefined => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').trim());
  return Number.isFinite(n) ? n : undefined;
};

/** True when this field drives a price. */
export function isPricedField(field: any): boolean {
  const pr = field?.pricing;
  return !!(field && field.type === 'number' && pr && (num(pr.firstPrice) !== undefined || num(pr.extraPrice) !== undefined));
}

/** True when this field repeats a sub-field per unit. */
export function hasRepeat(field: any): boolean {
  return !!(field && field.type === 'number' && field.repeat && (field.repeat.label || '').trim());
}

/** Read a quantity answer as a clean positive integer (0 = not filled). */
export function parseQuantity(raw: any, field?: any): number {
  const n = num(raw);
  if (n === undefined || n <= 0) return 0;
  let q = Math.floor(n);
  const min = num(field?.minValue);
  const max = num(field?.maxValue);
  if (min !== undefined && q < min) q = Math.max(q, 0); // don't silently inflate — validation reports it
  if (max !== undefined && q > max) q = max;
  return Math.min(q, QUANTITY_MAX);
}

/** Resolve the per-currency price pair (ILS fields are the fallback). */
export function pricesFor(pricing: QuantityPricing, currency?: string): { firstPrice: number; extraPrice: number; includedUnits: number } {
  const by = (currency && pricing.byCurrency?.[currency]) || undefined;
  return {
    firstPrice: num(by?.firstPrice) ?? num(pricing.firstPrice) ?? 0,
    extraPrice: num(by?.extraPrice) ?? num(pricing.extraPrice) ?? 0,
    includedUnits: Math.max(1, num(pricing.includedUnits) ?? 1),
  };
}

/** Price for one priced field at a given quantity. */
export function priceForQuantity(qty: number, pricing: QuantityPricing, currency?: string): number {
  if (!qty || qty <= 0) return 0;
  const { firstPrice, extraPrice, includedUnits } = pricesFor(pricing, currency);
  const extras = Math.max(0, qty - includedUnits);
  const total = firstPrice + extraPrice * extras;
  return Math.max(0, Math.round(total * 100) / 100);
}

/** How many repeat sub-fields a given quantity produces. */
export function repeatCount(qty: number, repeat: RepeatConfig): number {
  if (!qty || qty <= 0) return 0;
  const cap = Math.min(Math.max(1, num(repeat.max) ?? REPEAT_MAX), REPEAT_MAX);
  const n = repeat.skipFirst ? qty - 1 : qty;
  return Math.max(0, Math.min(n, cap));
}

/** 1-based unit indexes the repeat sub-fields represent. */
export function repeatIndexes(qty: number, repeat: RepeatConfig): number[] {
  const count = repeatCount(qty, repeat);
  const start = repeat.skipFirst ? 2 : 1;
  return Array.from({ length: count }, (_, i) => start + i);
}

/**
 * customFieldValues key for one repeat answer. Label-keyed on purpose —
 * see the module header. Falls back to the field id when the repeat label
 * is blank so a key is never just a bare number.
 */
export function repeatKey(field: any, index: number): string {
  const label = (field?.repeat?.label || '').trim() || field?.id || 'field';
  return `${label} ${index}`;
}

/** Every repeat key that could exist for a field, up to the safety cap. */
export function allRepeatKeys(field: any): string[] {
  const keys: string[] = [];
  for (let i = 1; i <= REPEAT_MAX + 1; i++) keys.push(repeatKey(field, i));
  return keys;
}

/** True when the repeat rows carry the small companion input. */
export function hasRepeatSecond(field: any): boolean {
  return hasRepeat(field) && !!(field.repeat.second && (field.repeat.second.label || '').trim());
}

/**
 * customFieldValues key for one companion answer ("גיל 2"). Falls back to a
 * derived key when the label is blank so it can never collide with the main
 * repeat key.
 */
export function repeatSecondKey(field: any, index: number): string {
  const label = (field?.repeat?.second?.label || '').trim()
    || `${(field?.repeat?.label || '').trim() || field?.id || 'field'} ב`;
  return `${label} ${index}`;
}

/** Every companion key that could exist for a field, up to the safety cap. */
export function allRepeatSecondKeys(field: any): string[] {
  const keys: string[] = [];
  for (let i = 1; i <= REPEAT_MAX + 1; i++) keys.push(repeatSecondKey(field, i));
  return keys;
}

/** Pull every priced/repeating field out of a page's blocks, in order. */
export function pricedFields(blocks: any[]): any[] {
  const out: any[] = [];
  for (const b of blocks || []) {
    if (b?.type !== 'form') continue;
    for (const f of (b.props?.formFields || [])) {
      if (isPricedField(f) || hasRepeat(f)) out.push(f);
    }
  }
  return out;
}

/**
 * Total price for a page. Returns null when the page has NO priced field —
 * that null is what keeps every existing campaign on the legacy amount path.
 */
export function computeQuantityTotal(
  blocks: any[],
  values: Record<string, any>,
  currency?: string,
): number | null {
  let found = false;
  let total = 0;
  for (const f of pricedFields(blocks)) {
    if (!isPricedField(f)) continue;
    found = true;
    total += priceForQuantity(parseQuantity(values?.[f.id], f), f.pricing, currency);
  }
  return found ? Math.round(total * 100) / 100 : null;
}

/** Human breakdown line, e.g. "₪50 למשתתף ראשון + ₪25 לכל נוסף". */
export function pricingHint(pricing: QuantityPricing, currency: string, symbol: string, locale: 'he' | 'en' = 'he'): string {
  const { firstPrice, extraPrice, includedUnits } = pricesFor(pricing, currency);
  const fmt = (n: number) => `${symbol}${n.toLocaleString(locale === 'en' ? 'en-US' : 'he-IL')}`;
  if (locale === 'en') {
    const first = includedUnits > 1 ? `first ${includedUnits}` : 'first';
    return extraPrice > 0 ? `${fmt(firstPrice)} for the ${first}, ${fmt(extraPrice)} for each additional` : `${fmt(firstPrice)}`;
  }
  const first = includedUnits > 1 ? `${includedUnits} הראשונים` : 'הראשון';
  return extraPrice > 0 ? `${fmt(firstPrice)} עבור ${first}, ${fmt(extraPrice)} לכל נוסף` : `${fmt(firstPrice)}`;
}
